import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ChevronLeft, ChevronRight, Plus, Trash2, Lock } from 'lucide-react';
import BlockSlotModal from './BlockSlotModal';
import BookingDetailModal from './BookingDetailModal';
import { useAdminRole } from '../hooks/UseAdminRole';
import AlertModal from './AlertModal';
import { useAlert } from '../hooks/UseAlert';

const AdminCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { isFullAdmin, isRestrictedAdmin, loading: roleLoading } = useAdminRole();

  const { success, error, warning, alertConfig, closeAlert } = useAlert();

  const openingHours = {
    1: null,
    2: { start: "14:00", end: "20:00" },
    3: { start: "11:00", end: "20:00" },
    4: { start: "11:00", end: "20:00" },
    5: { start: "11:00", end: "20:00" },
    6: { start: "10:00", end: "18:00" },
    0: null,
  };

  useEffect(() => {
    loadMonthBookings();
  }, [currentDate]);

  const loadMonthBookings = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const startDate = startOfMonth.toISOString().split('T')[0];
      const endDate = endOfMonth.toISOString().split('T')[0];

      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );

      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if(data.status === 'confirmed' || data.status === 'blocked') {
          bookingsData.push({ id: doc.id, ...data });
        }
      });

      setBookings(bookingsData);
    } catch (error) {
      console.error('Erreur chargement réservations:', error);
    }
  };

  const loadDayBookings = async (date) => {
    const dateStr = date.toLocaleDateString('fr-CA');
    const dayBookings = bookings.filter(b => b.date === dateStr);
    setDayBookings(dayBookings);
    setSelectedDate(date);
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!isFullAdmin) {
      error('Vous n\'avez pas les permissions pour supprimer des réservations');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'bookings', bookingId));
      success('Créneau supprimé');
      loadMonthBookings();
      if (selectedDate) {
        loadDayBookings(selectedDate);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      error('Erreur lors de la suppression');
    }
  };

  const handleBlockSuccess = async () => {
    await loadMonthBookings();
    if (selectedDate) {
      loadDayBookings(selectedDate);
    }
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const handleBookingUpdate = () => {
    loadMonthBookings();
    if (selectedDate) {
      loadDayBookings(selectedDate);
    }
  };

  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getAdminTimeSlotsForDate = (date) => {
    if (!date) return [];

    const day = date.getDay();
    const hours = openingHours[day];

    if (!hours) return [];

    const slots = [];
    let current = timeToMinutes(hours.start);
    const end = timeToMinutes(hours.end);

    while (current <= end) {
      slots.push(minutesToTime(current));
      current += 15;
    }

    return slots;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getBookingsForDate = (date) => {
    if (!date) return 0;
    const dateStr = date.toLocaleDateString('fr-CA');
    return bookings.filter(b => b.date === dateStr).length;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isSlotBooked = (time) => {
    return dayBookings.some(booking => booking.blockedSlots.includes(time));
  };

  const getBookingForSlot = (time) => {
    return dayBookings.find(booking => booking.timeSlot === time);
  };

  const dailyTimeSlots = selectedDate ? getAdminTimeSlotsForDate(selectedDate) : [];

  if (roleLoading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
      {showBlockModal && selectedDate && (
        <BlockSlotModal
          selectedDate={selectedDate}
          onClose={() => setShowBlockModal(false)}
          onSuccess={handleBlockSuccess}
          isRestrictedAdmin={isRestrictedAdmin}
        />
      )}

      {showDetailModal && selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedBooking(null);
          }}
          onDelete={handleDeleteBooking}
          onUpdate={handleBookingUpdate}
          isFullAdmin={isFullAdmin}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendrier mensuel */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-purple-900">
              {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-purple-600" />
              </button>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-purple-600" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {/* modif à faire pour commencer par lundi */}
            {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}

            {getDaysInMonth().map((date, index) => {
              const dateStr = date ? date.toLocaleDateString('fr-CA') : '';
              const bookingCount = date ? getBookingsForDate(date) : 0;
              const isToday = date && date.toDateString() === new Date().toDateString();
              const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
              const isBlockedDay = date && bookings.some(b => b.date === dateStr && b.blockType === 'multi');
              const isBeforeBookingStart = date && date < new Date('2026-02-09');

              return (
                <div
                  key={index}
                  onClick={() => date && loadDayBookings(date)}
                  className={`
                    aspect-square p-2 rounded-lg cursor-pointer transition-colors
                    ${!date ? 'invisible' : ''}
                    ${isToday ? 'bg-purple-100 border-2 border-purple-500' : ''}
                    ${isSelected ? 'bg-purple-200' : 'hover:bg-gray-50'}
                    ${isBlockedDay ? 'bg-gray-400 opacity-60 cursor-not-allowed' : ''}
                    ${isBeforeBookingStart ? 'bg-gray-200 opacity-40' : ''}
                  `}
                >
                  {date && (
                    <div className="h-full flex flex-col">
                      <div className="text-center font-medium">{date.getDate()}</div>
                      {bookingCount > 0 && (
                        <div className="mt-auto">
                          <div className="text-xs bg-purple-600 text-white rounded-full px-2 py-0.5 text-center">
                            {bookingCount}
                          </div>
                        </div>
                      )}
                      {isBlockedDay && (
                        <div className="text-xs text-white bg-red-500 rounded px-1 mt-1">
                          Bloqué
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Planning du jour */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-purple-900">
              {selectedDate
                ? selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                : 'Sélectionnez une date'}
            </h3>
            {selectedDate && (
              <button
                onClick={() => setShowBlockModal(true)}
                className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                title="Bloquer un créneau"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
          </div>

          {selectedDate && (
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {dailyTimeSlots.length === 0 && (
                <div className="text-sm text-gray-500 italic">Salon fermé.</div>
              )}

              {dailyTimeSlots.map((time) => {
                const isBooked = isSlotBooked(time);
                const booking = getBookingForSlot(time);

                return (
                  <div
                    key={time}
                    onClick={() => booking && handleBookingClick(booking)}
                    className={`
                      p-2 rounded text-sm
                      ${isBooked 
                        ? isRestrictedAdmin 
                          ? 'bg-gray-200 border-l-4 border-gray-400' 
                          : 'bg-red-100 border-l-4 border-red-500'
                        : 'bg-green-50'
                      }
                    `}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium">{time}</div>
                        
                        {/* Affichage selon le rôle */}
                        {booking && (
                          <div className="text-xs mt-1">
                            {isFullAdmin ? (
                              <>
                                <div className="font-semibold text-purple-900">{booking.serviceName}</div>
                                <div className="text-gray-600">{booking.userName}</div>
                                {booking.isAdminBlock && booking.clientName && (
                                  <div className="text-purple-600 italic">Pour: {booking.clientName}</div>
                                )}
                              </>
                            ) : (
                              <div className="text-gray-500 italic flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Créneau réservé
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Bouton supprimer uniquement pour admin complet */}
                      {booking && isFullAdmin && (
                        <button
                          onClick={() => handleDeleteBooking(booking.id)}
                          className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminCalendar;